export type DueStatus = 'MISSING' | 'OK' | 'DUE_SOON' | 'OVERDUE' | 'COMPLETED' | 'INACTIVE';

export interface CompanyMaintenanceDashboard {
  metrics: CompanyMaintenanceMetrics;
  vehicles: VehicleDueItem[];
}

export interface CompanyMaintenanceMetrics {
  vehiclesTracked: number;
  expiringInsuranceCount: number;
  overdueInsuranceCount: number;
  expiringInspectionCount: number;
  overdueInspectionCount: number;
  expiringTaxCount: number;
  overdueTaxCount: number;
  dueServiceCount: number;
  overdueServiceCount: number;
}

export interface VehicleDueItem {
  vehicleId: number;
  vehicleLabel: string;
  currentMileageKm?: number | null;
  insurance: RecordSummary;
  inspection: RecordSummary;
  tax: RecordSummary;
  dueServiceSchedules: ServiceSchedule[];
}

export interface VehicleMaintenanceOverview {
  vehicle: VehicleMaintenanceHeader;
  mileage: VehicleMileageSummary;
  insurance: RecordSummary;
  inspection: RecordSummary;
  tax: RecordSummary;
  insuranceRecords: InsuranceRecord[];
  inspectionRecords: InspectionRecord[];
  taxRecords: TaxRecord[];
  serviceSchedules: ServiceSchedule[];
  serviceHistory: ServiceCompletion[];
  customEvents: VehicleHistoryEvent[];
  mileageHistory: VehicleMileageHistoryEntry[];
  attachments: VehicleAttachment[];
  timeline: TimelineEntry[];
}

export interface VehicleMaintenanceHeader {
  id: number;
  make: string;
  model: string;
  year: number;
  licensePlate?: string | null;
  currentMileageKm?: number | null;
  status?: string | null;
}

export interface RecordSummary {
  status: DueStatus;
  effectiveDate?: string | null;
  expiryDate?: string | null;
  daysRemaining?: number | null;
  amount?: number | string | null;
  label?: string | null;
  secondaryLabel?: string | null;
}

export interface VehicleMileageSummary {
  currentMileageKm?: number | null;
  lastUpdatedAt?: string | null;
  lastUpdatedBy?: string | null;
  lastSource?: string | null;
  lastReference?: string | null;
  totalRecordedUpdates: number;
}

export interface InsuranceRecord {
  id: number;
  policyNumber: string;
  providerCompany: string;
  startDate: string;
  expiryDate: string;
  cost?: number | string | null;
  notes?: string | null;
  createdAt?: string | null;
}

export interface InspectionRecord {
  id: number;
  inspectionDate: string;
  expiryDate: string;
  inspectionCenter: string;
  cost?: number | string | null;
  notes?: string | null;
  createdAt?: string | null;
}

export interface TaxRecord {
  id: number;
  taxType: string;
  paymentDate: string;
  expiryDate: string;
  amount?: number | string | null;
  notes?: string | null;
  createdAt?: string | null;
}

export interface ServiceSchedule {
  id: number;
  serviceType: string;
  serviceTaskLabel: string;
  scheduleMode: string;
  dueRuleType: string;
  recurrenceKilometersKm?: number | null;
  recurrenceMonths?: number | null;
  initialDueDate?: string | null;
  initialDueMileageKm?: number | null;
  nextDueDate?: string | null;
  nextDueMileageKm?: number | null;
  lastCompletedOn?: string | null;
  lastCompletedMileageKm?: number | null;
  workshopProvider?: string | null;
  dueStatus: DueStatus;
  notes?: string | null;
  status: string;
  createdAt?: string | null;
  sourceTemplateId?: number | null;
  sourceTemplateName?: string | null;
}

export interface ServiceCompletion {
  id: number;
  serviceScheduleId?: number | null;
  serviceType: string;
  serviceTaskLabel: string;
  completedOn: string;
  completedMileageKm?: number | null;
  cost?: number | string | null;
  workshopProvider?: string | null;
  notes?: string | null;
  createdAt?: string | null;
}

export interface VehicleHistoryEvent {
  id: number;
  category: string;
  title: string;
  eventDate: string;
  mileageKm?: number | null;
  cost?: number | string | null;
  providerName?: string | null;
  notes?: string | null;
  createdAt?: string | null;
}

export interface VehicleMileageHistoryEntry {
  id: number;
  previousMileageKm?: number | null;
  newMileageKm: number;
  differenceKm: number;
  source: string;
  sourceType?: string | null;
  sourceId?: number | null;
  reference?: string | null;
  updatedByUserId?: number | null;
  updatedByDisplayName?: string | null;
  updatedAt: string;
  notes?: string | null;
}

export interface VehicleAttachment {
  id: number;
  ownerType: string;
  ownerRecordId: number;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  fileUrl: string;
  notes?: string | null;
  createdAt?: string | null;
}

export interface TimelineEntry {
  sourceType: string;
  sourceId: number;
  category: string;
  title: string;
  subtitle?: string | null;
  occurredOn: string;
  mileageKm?: number | null;
  amount?: number | string | null;
  notes?: string | null;
}

export enum ServiceTaskType {
  OIL_CHANGE = 'OIL_CHANGE',
  FILTERS = 'FILTERS',
  BRAKES = 'BRAKES',
  TIRES = 'TIRES',
  TIMING_BELT = 'TIMING_BELT',
  BATTERY = 'BATTERY',
  INSPECTION = 'INSPECTION',
  ENGINE = 'ENGINE',
  TRANSMISSION = 'TRANSMISSION',
  SUSPENSION = 'SUSPENSION',
  COOLANT = 'COOLANT',
  CUSTOM = 'CUSTOM',
}

export enum ServiceScheduleMode {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum ServiceDueRuleType {
  CALENDAR = 'CALENDAR',
  MILEAGE = 'MILEAGE',
  MILEAGE_OR_CALENDAR = 'MILEAGE_OR_CALENDAR',
}

export enum ServiceScheduleStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
}

export enum VehicleHistoryEventCategory {
  TIRE_REPLACEMENT = 'TIRE_REPLACEMENT',
  ACCIDENT_REPAIR = 'ACCIDENT_REPAIR',
  BATTERY_REPLACEMENT = 'BATTERY_REPLACEMENT',
  WINDSHIELD_REPLACEMENT = 'WINDSHIELD_REPLACEMENT',
  MAJOR_MAINTENANCE = 'MAJOR_MAINTENANCE',
  ENGINE_WORK = 'ENGINE_WORK',
  CUSTOM = 'CUSTOM',
  OTHER = 'OTHER',
}

export interface CreateInsuranceRecordRequest {
  policyNumber: string;
  providerCompany: string;
  startDate: string;
  expiryDate: string;
  cost?: number | null;
  notes?: string;
}

export interface UpdateInsuranceRecordRequest {
  policyNumber: string;
  providerCompany: string;
  startDate: string;
  expiryDate: string;
  cost?: number | null;
  notes?: string;
}

export interface CreateInspectionRecordRequest {
  inspectionDate: string;
  expiryDate: string;
  inspectionCenter: string;
  cost?: number | null;
  notes?: string;
}

export interface UpdateInspectionRecordRequest {
  inspectionDate: string;
  expiryDate: string;
  inspectionCenter: string;
  cost?: number | null;
  notes?: string;
}

export interface CreateTaxRecordRequest {
  taxType: string;
  paymentDate: string;
  expiryDate: string;
  amount?: number | null;
  notes?: string;
}

export interface UpdateTaxRecordRequest {
  taxType: string;
  paymentDate: string;
  expiryDate: string;
  amount?: number | null;
  notes?: string;
}

export interface CreateServiceScheduleRequest {
  serviceType: ServiceTaskType;
  serviceTaskLabel: string;
  description?: string;
  scheduleMode: ServiceScheduleMode;
  dueRuleType: ServiceDueRuleType;
  initialDueDate?: string;
  initialDueMileageKm?: number | null;
  recurrenceKilometersKm?: number | null;
  recurrenceMonths?: number | null;
  workshopProvider?: string;
  notes?: string;
}

export interface CreateServiceCompletionRequest {
  serviceScheduleId?: number | null;
  serviceType?: ServiceTaskType | null;
  serviceTaskLabel?: string;
  completedOn: string;
  completedMileageKm?: number | null;
  cost?: number | null;
  workshopProvider?: string;
  notes?: string;
}

export interface UpdateServiceCompletionRequest {
  serviceScheduleId?: number | null;
  serviceType?: ServiceTaskType | null;
  serviceTaskLabel?: string;
  completedOn: string;
  completedMileageKm?: number | null;
  cost?: number | null;
  workshopProvider?: string;
  notes?: string;
}

export interface UpdateServiceScheduleStatusRequest {
  status: ServiceScheduleStatus;
  notes?: string;
}

export interface CreateVehicleHistoryEventRequest {
  category: VehicleHistoryEventCategory;
  title: string;
  eventDate: string;
  mileageKm?: number | null;
  cost?: number | null;
  providerName?: string;
  notes?: string;
}

export interface UpdateVehicleHistoryEventRequest {
  category: VehicleHistoryEventCategory;
  title: string;
  eventDate: string;
  mileageKm?: number | null;
  cost?: number | null;
  providerName?: string;
  notes?: string;
}

export interface UpdateVehicleMileageRequest {
  currentMileageKm: number;
  notes?: string;
}
